/**
 * Просмотр модели по наведению на карточку каталога: задержал мышь на карточке — рядом
 * всплывает её 3D-модель (`ModelPeek`), увёл — пропала. Задержка — чтобы окошко не мигало,
 * пока мышь просто едет по сетке. Только мышь: на пальце наведения нет, там «глазик».
 * Нажали на карточку (выбрать, потащить) — просмотр закрывается.
 */
import { useRef, useState, type PointerEvent } from "react";
import type { CatalogItem } from "@/shared/catalog";

/** Сколько держать мышь на карточке, чтобы открылась модель, мс */
const PEEK_DELAY_MS = 400;

export interface ModelPeekTarget {
	/** Предмет каталога: модель для сцены и габариты для подписи */
	item: CatalogItem;
	title: string;
	/** Где карточка на экране — рядом с ней встаёт окошко */
	anchor: { left: number; right: number; top: number };
}

export const useModelPeek = () => {
	const [peek, setPeek] = useState<ModelPeekTarget | null>(null);
	const timer = useRef<number | undefined>(undefined);

	const closePeek = () => {
		window.clearTimeout(timer.current);
		setPeek(null);
	};

	/** Обработчики для обёртки карточки */
	const peekProps = (item: CatalogItem, title: string) => ({
		onPointerEnter: (event: PointerEvent<HTMLElement>) => {
			window.clearTimeout(timer.current);
			if (event.pointerType !== "mouse") return;
			const { left, right, top } = event.currentTarget.getBoundingClientRect();
			timer.current = window.setTimeout(
				() => setPeek({ item, title, anchor: { left, right, top } }),
				PEEK_DELAY_MS,
			);
		},
		onPointerLeave: closePeek,
		onPointerDown: closePeek,
	});

	return { peek, peekProps, closePeek };
};
